import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { messageAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Mail, 
  Bell, 
  Archive, 
  CheckCircle, 
  ArrowRight,
  Inbox,
  AlertTriangle
} from 'lucide-react';

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [summaryRes, messagesRes] = await Promise.all([
        messageAPI.getSummary(),
        messageAPI.getMessages({ size: 5 })
      ]);
      setSummary(summaryRes.data);
      setRecentMessages(messagesRes.data.messages || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's an overview of your messages
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate('/inbox')} data-testid="view-inbox-btn">
            <Inbox className="w-4 h-4 mr-2" />
            View Inbox
          </Button>
          <Button variant="outline" onClick={() => navigate('/alerts')} data-testid="view-alerts-btn">
            <Bell className="w-4 h-4 mr-2" />
            View Alerts
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card 
          className="cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-1"
          onClick={() => navigate('/inbox?status=unread')}
          data-testid="stat-unread"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Unread</p>
                <p className="text-3xl font-heading font-bold mt-2">{summary?.unread || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-1"
          onClick={() => navigate('/inbox?status=read')}
          data-testid="stat-read"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Read</p>
                <p className="text-3xl font-heading font-bold mt-2">{summary?.read || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-1"
          onClick={() => navigate('/inbox?status=archived')}
          data-testid="stat-archived"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Archived</p>
                <p className="text-3xl font-heading font-bold mt-2">{summary?.archived || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Archive className="w-6 h-6 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-1"
          onClick={() => navigate('/inbox')}
          data-testid="stat-total"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total</p>
                <p className="text-3xl font-heading font-bold mt-2">{summary?.total || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                <Inbox className="w-6 h-6 text-secondary-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Messages */}
      <Card data-testid="recent-messages">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Recent Messages</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/inbox')}>
            View all <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {recentMessages.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No messages yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentMessages.map((message) => (
                <div
                  key={message.message_id}
                  className={`p-4 rounded-md cursor-pointer transition-all duration-200 hover:bg-accent/10 hover:pl-6 ${
                    !message.read ? 'bg-primary/5 border-l-4 border-primary' : 'border-l-4 border-transparent'
                  }`}
                  onClick={() => navigate(`/messages/${message.message_id}`)}
                  data-testid={`message-item-${message.message_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.type === 'alert' 
                          ? 'bg-accent/10' 
                          : 'bg-primary/10'
                      }`}>
                        {message.type === 'alert' ? (
                          <AlertTriangle className="w-5 h-5 text-accent" />
                        ) : (
                          <Mail className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className={`font-medium truncate ${!message.read ? 'font-semibold' : ''}`}>
                            {message.subject}
                          </h4>
                          {!message.read && (
                            <Badge variant="secondary" className="text-xs">New</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          From: {message.source}
                        </p>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(message.sent_date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
